import React, { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import './LoadingScreen.css'
import { getAudioVolume } from '../../utils/CookiesUtils'

// Splash / loading screen, rebuilt from ice-assets/loading/loading.mp4.
// The choreography and the sprite-sheet layouts are documented in
// LoadingScreen.css — this file only places the pieces.
const ASSETS = `${process.env.PUBLIC_URL}/ice-assets/loading`
const MUSIC = `${process.env.PUBLIC_URL}/assets/ice-bonus/abertura.wav`

// abertura.wav runs 7.1s and the screen can stay up longer than that, so it
// repeats. Set this to false if you would rather the opening sting played once.
const LOOP_MUSIC = true

// How long the splash holds before it hands over to the menu. The choreography's
// last one-shot is the penguin's dive, which lands at 6.85s (see the timeline at
// the top of LoadingScreen.css), so this leaves a beat to read him in the hole
// before the screen changes. Everything still running past that point is an idle
// loop, so there is nothing else to wait for.
const HOLD_MS = 8000

const sheet = (name) => ({ backgroundImage: `url(${ASSETS}/sprites/${name}.png)` })

function LoadingScreen({ onDone }) {
    const navigate = useNavigate()

    /* When the animation has played out, go straight into the game. `replace` so
       the back button returns to the lobby rather than bouncing through the
       splash again. A parent can take over by passing onDone. */
    useEffect(() => {
        const timer = setTimeout(() => {
            if (onDone) onDone()
            else navigate('/halloween-ice', { replace: true })
        }, HOLD_MS)
        return () => clearTimeout(timer)
    }, [navigate, onDone])

    /* Background music, at the volume the player has set for the game.
       The .catch is not the whole story here: this screen is usually the first
       thing shown, and browsers refuse to start audio before the page has been
       interacted with, so on a cold load play() rejects. When it does, the
       track is armed to start on the first tap or key instead of being lost. */
    useEffect(() => {
        const music = new Audio(MUSIC)
        music.loop = LOOP_MUSIC
        music.volume = getAudioVolume()

        // play() is a promise, and it can settle AFTER this screen is gone —
        // React runs the effect twice on mount in development, and any quick
        // navigation does the same. Without this flag the late rejection arms a
        // listener that the cleanup has already run past, and the orphaned track
        // starts on the next tap: two copies of the music at once.
        let gone = false
        let disarm = () => { }

        music.play().catch(() => {
            if (gone) return
            const start = () => {
                if (!gone) music.play().catch(() => { })
                disarm()
            }
            window.addEventListener('pointerdown', start, { once: true })
            window.addEventListener('keydown', start, { once: true })
            disarm = () => {
                window.removeEventListener('pointerdown', start)
                window.removeEventListener('keydown', start)
            }
        })

        return () => {
            gone = true
            disarm()
            music.pause()
            music.currentTime = 0
        }
    }, [])

    return (
        <div
            className='loading-screen-wrapper'
            style={{ backgroundImage: `url(${ASSETS}/background.png)` }}
        >
            {/* Only the rope puller rides inside the logo's box — it holds the
                tow rope, which is part of logo.png, so it has to travel with it.
                The other three are pinned to the scenery (a fir, a shore, a
                ridge) and stay put while the logo is dragged past them. */}
            <div className='ls-logo-group'>
                <img className='ls-logo' src={`${ASSETS}/logo.png`} alt='Era do Gelo' />
                <div className='ls-peng ls-peng-drag' style={sheet('spritePenguin001')} aria-hidden='true' />
            </div>

            {/* the snow-capped stump on the near bank — scenery, drawn over the
                logo's snow base, which is why it is not in the background image */}
            <div
                className='ls-tree'
                style={{ backgroundImage: `url(${ASSETS}/tree.png)` }}
                aria-hidden='true'
            />

            {/* over the left ridge, onto the "E" of ERA */}
            <div className='ls-peng ls-peng-slope' style={sheet('spritePenguin002')} aria-hidden='true' />
            {/* down off the fir, stops beside the "O" of DO. Two elements for one
                bird: the poles pose and the standing pose need different framings
                of the sheet — see LoadingScreen.css */}
            <div className='ls-peng ls-peng-ski' style={sheet('spritePenguin003')} aria-hidden='true' />
            <div className='ls-peng ls-peng-ski-idle' style={sheet('spritePenguin003')} aria-hidden='true' />
            {/* up from the shore and straight into that "O" — again two elements,
                because the frames it kicks with once it is stuck in there sit at
                slices this sheet's even split cannot address */}
            <div className='ls-peng ls-peng-hole' style={sheet('spritePenguin004')} aria-hidden='true' />
            <div className='ls-peng ls-peng-hole-kick' style={sheet('spritePenguin004')} aria-hidden='true' />
        </div>
    )
}

export default LoadingScreen
